import { FC } from 'react'
import { Stack, Typography } from '@mui/material'
import { Activity, OutdoorEngagement } from '../../../../api/models'
import { getTimelineEndColor, SectionText } from './customerCareTimelineUtils'
import { formatTimelineTime } from '../ertUtils'
import { TimelineSection } from './TimelineSection'
import { EMPTY_TIMELINE_TEXT, ERROR_TEXT } from '../constants'

interface TimelineProps {
  // undefined when the engagement request failed
  engagement: OutdoorEngagement | undefined
  // undefined when the activities request failed
  activities: Activity[] | undefined
  // empty when the engagement or activities request failed
  fragments: SectionText[]
  timezone?: string
}

export const ENGAGEMENT_TIMELINE_HEADER = 'Event Timeline'

export const Timeline: FC<TimelineProps> = ({ engagement, activities, fragments, timezone }) => {
  const hasData = !!engagement && !!activities
  const endColor = hasData ? getTimelineEndColor(engagement, activities) : undefined
  const lastIndex = fragments.length - 1

  return (
    <Stack
      sx={{
        gap: 'var(--spacing-m)',
        padding: 'var(--spacing-m)',
        width: '100%',
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--border-radius-xl)',
        boxShadow: '0px 1px 2px 0px rgba(28,25,23,0.05)'
      }}
      data-testid="engagement-timeline"
    >
      <Typography
        data-testid="engagement-timeline-header"
        sx={{ color: 'var(--text-primary)', fontSize: 'var(--size-s)', fontWeight: 500, lineHeight: 'var(--line-height-s)' }}
      >
        {ENGAGEMENT_TIMELINE_HEADER}
      </Typography>
      {!hasData && (
        <Typography
          data-testid="engagement-timeline-error"
          sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-xs)', lineHeight: 'var(--line-height-xs)', textAlign: 'center' }}
        >
          {ERROR_TEXT}
        </Typography>
      )}
      {hasData && fragments.length === 0 && (
        <Typography
          data-testid="engagement-timeline-empty"
          sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-xs)', lineHeight: 'var(--line-height-xs)', textAlign: 'center' }}
        >
          {EMPTY_TIMELINE_TEXT}
        </Typography>
      )}
      <Stack sx={{ width: '100%' }}>
        {fragments.map((fragment, index) => (
          <TimelineSection
            key={fragment.header}
            time={formatTimelineTime(fragment.time, timezone)}
            title={fragment.header}
            detail={fragment.subtext}
            endColor={index === lastIndex ? endColor : undefined}
            showConnector={index !== lastIndex}
            data-testid={`timeline-section-${index}`}
          />
        ))}
      </Stack>
    </Stack>
  )
}

Timeline.displayName = 'Timeline'
